import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useMemo, useContext, useRef } from 'react';
import ReactDOM from 'react-dom';
import cn from 'classnames';
import { useDevice } from '../../providers/Device';
import { useIsInBottomSheet } from '../../providers/IsInBottomSheetProvider';
import { OverlayContext, OverlayToastsTestIds } from '../../providers/OverlayContextProvider';
import { useTheme } from '../../providers/Theme';
import { generateUniqueId } from '../../utils/generateUniqueId';
import { useIsMounted } from '../../utils/useIsMounted';
import { useStateInRef } from '../../utils/useStateInRef';
import { useBiContextGetter } from '../BiTracking/BiContext';
import { OverlayContainerProvider } from '../BottomSheet/BottomSheetContainerContext';
import { ConfirmationModal } from '../ConfirmationModal';
import { Toast } from '../Toast';
import { ToastPresentationContext } from '../Toast/ToastPresentationContext';
import { BottomSheetContainer } from './BottomSheetContainer/BottomSheetContainer';
import { GlobalLoader } from './GlobalLoader/GlobalLoader';
import { useImperativeSheetPages } from './hooks/useImperativeSheetPages';
import { useOverlayEntries } from './hooks/useOverlayEntries';
import { useSheetRunRegistry } from './hooks/useSheetRunRegistry';
import { ModalContainer } from './ModalContainer/ModalContainer';
import styles from './OverlayContext.module.scss';
import { groupOverlayEntries } from './utils/groupOverlayEntries/groupOverlayEntries';
export const OVERLAY_CONTAINER_ROOT_TEST_ID = 'overlay-container-root';
export function OverlayProvider(props) {
    const { children, ModalWrapper, isTestMode = false, disableEnforceFocus, isContainer } = props;
    const [container, setContainer] = useState(null);
    const isMounted = useIsMounted();
    const overlayEntries = useOverlayEntries();
    const { entries: modals, updateEntries, getUpdateEntries, readOpenDialogs } = overlayEntries;
    const sheetRuns = useSheetRunRegistry();
    const [loaders, setLoaders] = useState({});
    const [toasts, setToasts] = useState([]);
    const zIndexSeqRef = useRef(0);
    const [selfRenderedOverlayCount, setSelfRenderedOverlayCount] = useState(0);
    const [closingRunIds, setClosingRunIds] = useState([]);
    const somethingIsLoading = Object.values(loaders).some((count)=>count > 0);
    const shouldBlurBackground = modals.some((modal)=>modal.shouldBlurBackground);
    useEffect(()=>{
        return ()=>{
            setToasts((prevToasts)=>{
                prevToasts.forEach((toast)=>clearTimeout(toast.timer));
                return prevToasts;
            });
        };
    }, []);
    useEffect(()=>{
        if (modals.length === 0 && selfRenderedOverlayCount === 0) {
            zIndexSeqRef.current = 0;
        }
    }, [
        modals.length,
        selfRenderedOverlayCount
    ]);
    // WHY: closing the run rather than dropping it keeps its pages mounted through the drawer's exit transition, which is the whole difference between hideBottomSheet and a pop.
    useEffect(()=>{
        if (closingRunIds.length === 0) return;
        const closing = new Set(closingRunIds);
        setClosingRunIds([]);
        getUpdateEntries()((previousEntries)=>previousEntries.map((entry)=>entry.sheetRunId != null && closing.has(entry.sheetRunId) ? _object_spread_props(_object_spread({}, entry), {
                    isOpen: false
                }) : entry));
    }, [
        closingRunIds,
        getUpdateEntries
    ]);
    const sheetPages = useImperativeSheetPages({
        entries: overlayEntries,
        sheetRuns,
        nextZIndexOrder: ()=>zIndexSeqRef.current++,
        closeRunWithSlideOut: (runId)=>setClosingRunIds((previousRunIds)=>previousRunIds.concat(runId))
    });
    const getSheetPages = useStateInRef(sheetPages);
    const staticMethods = useMemo(()=>{
        function nextZIndexOrder() {
            return zIndexSeqRef.current++;
        }
        function claimZIndexOrder() {
            setSelfRenderedOverlayCount((count)=>count + 1);
            return nextZIndexOrder();
        }
        function releaseZIndexOrder() {
            setSelfRenderedOverlayCount((count)=>count - 1);
        }
        function showToast(content) {
            let { isMobileDevice = false, persistent = false, aboveGlobalLoader = false } = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
            const defaultDuration = 5;
            const id = generateUniqueId(persistent ? 'persistent-toast' : 'toast');
            const transitionDuration = persistent ? '0.2s' : "".concat(defaultDuration * 0.95, "s");
            const dismiss = ()=>{
                if (!isMounted()) return;
                setToasts((prevToasts)=>prevToasts.filter((toast)=>toast.id !== id));
            };
            const timer = persistent ? undefined : setTimeout(dismiss, defaultDuration * 1000);
            setToasts((prevToasts)=>prevToasts.concat({
                    content,
                    id,
                    transitionDuration,
                    timer,
                    isMobileDevice,
                    persistent,
                    aboveGlobalLoader
                }));
            return ()=>{
                clearTimeout(timer);
                dismiss();
            };
        }
        const showModal = (params)=>{
            return new Promise((resolve)=>{
                getUpdateEntries()((prevModals)=>{
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const newModal = _object_spread_props(_object_spread({}, params), {
                        id: generateUniqueId('modal'),
                        isOpen: true,
                        resolve,
                        dismissModal: ()=>{},
                        zIndexOrder: nextZIndexOrder()
                    });
                    return prevModals.concat(newModal);
                });
            });
        };
        const showConfirmAction = async (args)=>{
            const { testId, onConfirm, title, content, primaryButtonLabel, secondaryButtonLabel, textAlignCenter = false, width, skin, openBottomSheetOnMobile, hideBottomSheetOnDismiss } = args;
            const biProps = args.noBi ? {
                noBi: args.noBi
            } : {
                biName: args.biName,
                biData: args.biData
            };
            await showModal(_object_spread_props(_object_spread({}, biProps), {
                closeOnClickOutside: true,
                closeOnEscapeKey: true,
                content: (param)=>{
                    let { dismissModal } = param;
                    return /*#__PURE__*/ _jsx(ConfirmationModal, {
                        // eslint-disable-next-line custom/no-hardcoded-nobi-in-seagull -- modal BI is opt-in via showModal/showConfirmAction; absent biName => no tracking by design
                        noBi: true,
                        testId: testId,
                        skin: skin,
                        title: title,
                        content: content,
                        openBottomSheetOnMobile: openBottomSheetOnMobile,
                        hideBottomSheetOnDismiss: hideBottomSheetOnDismiss,
                        textAlignCenter: textAlignCenter,
                        width: width,
                        primaryButtonLabel: primaryButtonLabel,
                        secondaryButtonLabel: secondaryButtonLabel,
                        onPrimaryButtonClick: async ()=>{
                            await (onConfirm === null || onConfirm === void 0 ? void 0 : onConfirm());
                            dismissModal(true);
                        },
                        onSecondaryButtonClick: ()=>dismissModal(false)
                    });
                }
            }));
        };
        return {
            getHasOpenBottomSheet: function getHasOpenBottomSheet() {
                for(var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++){
                    args[_key] = arguments[_key];
                }
                return getSheetPages().getHasOpenBottomSheet(...args);
            },
            getOpenedModals () {
                return readOpenDialogs();
            },
            showModal,
            showConfirmAction,
            showToast,
            hideModal (value) {
                const openDialogs = readOpenDialogs();
                if (!openDialogs.length) return;
                openDialogs[openDialogs.length - 1].dismissModal(value);
            },
            hideAllModals (value) {
                for (const modal of readOpenDialogs()){
                    modal.dismissModal(value);
                }
            },
            startLoader: (name)=>{
                const actualName = name || 'undefined';
                setLoaders((prevLoaders)=>_object_spread_props(_object_spread({}, prevLoaders), {
                        [actualName]: (prevLoaders[actualName] || 0) + 1
                    }));
            },
            stopLoader: (name)=>{
                const actualName = name || 'undefined';
                setLoaders((prevLoaders)=>_object_spread_props(_object_spread({}, prevLoaders), {
                        [actualName]: Math.max(0, (prevLoaders[actualName] || 0) - 1)
                    }));
            },
            showErrorToast: (text, testId)=>showToast(/*#__PURE__*/ _jsx(Toast, {
                    type: "error",
                    text: text,
                    testId: testId
                })),
            showSuccessToast: (text, testId)=>showToast(/*#__PURE__*/ _jsx(Toast, {
                    type: "success",
                    text: text,
                    testId: testId
                })),
            showWarningToast: (text, testId)=>showToast(/*#__PURE__*/ _jsx(Toast, {
                    type: "warning",
                    text: text,
                    testId: testId
                })),
            showInfoToast: (text, testId, options)=>showToast(/*#__PURE__*/ _jsx(Toast, {
                    type: "info",
                    text: text,
                    testId: testId,
                    action: options === null || options === void 0 ? void 0 : options.action
                })),
            showPersistentToast: (content, options)=>showToast(content, {
                    persistent: true,
                    aboveGlobalLoader: options === null || options === void 0 ? void 0 : options.aboveGlobalLoader
                }),
            showBottomSheet: function showBottomSheet() {
                for(var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++){
                    args[_key] = arguments[_key];
                }
                return getSheetPages().showBottomSheet(...args);
            },
            pushBottomSheetPage: function pushBottomSheetPage() {
                for(var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++){
                    args[_key] = arguments[_key];
                }
                return getSheetPages().pushBottomSheetPage(...args);
            },
            popBottomSheetPage: function popBottomSheetPage() {
                for(var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++){
                    args[_key] = arguments[_key];
                }
                return getSheetPages().popBottomSheetPage(...args);
            },
            removeBottomSheetPage: function removeBottomSheetPage() {
                for(var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++){
                    args[_key] = arguments[_key];
                }
                return getSheetPages().removeBottomSheetPage(...args);
            },
            hideBottomSheet: function hideBottomSheet() {
                for(var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++){
                    args[_key] = arguments[_key];
                }
                return getSheetPages().hideBottomSheet(...args);
            },
            claimZIndexOrder,
            releaseZIndexOrder
        };
    }, [
        getSheetPages,
        getUpdateEntries,
        readOpenDialogs
    ]);
    const contextValue = useMemo(()=>_object_spread_props(_object_spread({}, staticMethods), {
            isTestMode
        }), [
        isTestMode,
        staticMethods
    ]);
    function onModalExited(id) {
        sheetRuns.forgetEntries([
            id
        ]);
        updateEntries((prevModals)=>prevModals.filter((m)=>m.id !== id));
    }
    function triggerClose(id) {
        updateEntries((prevModals)=>prevModals.map((m)=>m.id === id ? _object_spread_props(_object_spread({}, m), {
                    isOpen: false
                }) : m));
    }
    return /*#__PURE__*/ _jsx(OverlayContext.Provider, {
        value: contextValue,
        children: /*#__PURE__*/ _jsxs(OverlayContainerProvider, {
            element: container,
            children: [
                isContainer ? /*#__PURE__*/ _jsxs("div", {
                    className: styles.overlayContainerScope,
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: cn(styles.overlayContext, {
                                [styles.blur]: shouldBlurBackground
                            }),
                            children: children
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            ref: setContainer,
                            className: styles.overlayContainerRoot,
                            "data-testid": OVERLAY_CONTAINER_ROOT_TEST_ID
                        })
                    ]
                }) : /*#__PURE__*/ _jsx("div", {
                    className: cn(styles.overlayContext, {
                        [styles.blur]: shouldBlurBackground
                    }),
                    children: children
                }),
                groupOverlayEntries(modals).map((group)=>group.kind === 'sheet' ? /*#__PURE__*/ _jsx(BottomSheetContainer, {
                        entries: group.entries,
                        // WHY: the drawer ranks by its newest page, as the managed stack did — ranking by the run's first page would let an overlay opened after a drill-down cover the drawer.
                        zIndexOrder: group.entries[group.entries.length - 1].zIndexOrder,
                        triggerClose: triggerClose,
                        onModalExited: onModalExited
                    }, group.key) : /*#__PURE__*/ _jsx(ModalContainer, {
                        modal: group.entry,
                        zIndexOrder: group.entry.zIndexOrder,
                        ModalWrapper: ModalWrapper,
                        isTestMode: isTestMode,
                        disableEnforceFocus: disableEnforceFocus,
                        triggerClose: triggerClose,
                        onModalExited: onModalExited
                    }, group.key)),
                /*#__PURE__*/ _jsx("div", {
                    "data-testid": OverlayToastsTestIds.Container(),
                    className: cn(styles.toastsContainer, {
                        [styles.mobileToastsContainer]: toasts.some((t)=>t.isMobileDevice),
                        [styles.aboveGlobalLoaderContainer]: toasts.some((t)=>t.aboveGlobalLoader)
                    }),
                    children: toasts.map((param)=>{
                        let { id, content, transitionDuration, isMobileDevice, persistent } = param;
                        return /*#__PURE__*/ _jsx("div", {
                            className: cn(styles.toast, {
                                [styles.mobileToast]: isMobileDevice,
                                [styles.persistentToast]: persistent
                            }),
                            style: {
                                '--toastDuration': transitionDuration
                            },
                            children: /*#__PURE__*/ _jsx(ToastPresentationContext.Provider, {
                                value: isMobileDevice,
                                children: content
                            })
                        }, id);
                    })
                }),
                somethingIsLoading && /*#__PURE__*/ ReactDOM.createPortal(/*#__PURE__*/ _jsx(GlobalLoader, {
                    scoped: container != null
                }), container !== null && container !== void 0 ? container : document.body)
            ]
        })
    });
}
export function useOverlayContext() {
    const getTheme = useStateInRef(useTheme());
    const getDevice = useStateInRef(useDevice());
    const isInBottomSheet = useIsInBottomSheet();
    const getIsMobileMode = useStateInRef(getDevice().isMobileDevice || isInBottomSheet);
    const internalContext = useContext(OverlayContext);
    const getBiContext = useBiContextGetter();
    return useMemo(()=>{
        const { showModal, showToast, showErrorToast: _showErrorToast, showSuccessToast: _showSuccessToast, showWarningToast: _showWarningToast, showInfoToast: _showInfoToast, showPersistentToast: _showPersistentToast } = internalContext, rest = _object_without_properties(internalContext, [
            "showModal",
            "showToast",
            "showErrorToast",
            "showSuccessToast",
            "showWarningToast",
            "showInfoToast",
            "showPersistentToast"
        ]);
        function showTypedToast(type, text, testId, action) {
            const isMobileMode = getIsMobileMode();
            return showToast(/*#__PURE__*/ _jsx(Toast, {
                type: type,
                text: text,
                testId: testId,
                isMobileDevice: isMobileMode,
                action: action
            }), {
                isMobileDevice: isMobileMode
            });
        }
        return _object_spread_props(_object_spread({}, rest), {
            showModal: (config)=>{
                var _config_biCallerContext;
                return showModal(_object_spread_props(_object_spread({}, config), {
                    theme: getTheme(),
                    device: getDevice(),
                    biCallerContext: (_config_biCallerContext = config.biCallerContext) !== null && _config_biCallerContext !== void 0 ? _config_biCallerContext : getBiContext
                }));
            },
            showErrorToast: (text, testId)=>{
                showTypedToast('error', text, testId);
            },
            showSuccessToast: (text, testId)=>{
                showTypedToast('success', text, testId);
            },
            showWarningToast: (text, testId)=>{
                showTypedToast('warning', text, testId);
            },
            showInfoToast: (text, testId, options)=>showTypedToast('info', text, testId, options === null || options === void 0 ? void 0 : options.action),
            showPersistentToast: (content, options)=>showToast(content, {
                    persistent: true,
                    isMobileDevice: (options === null || options === void 0 ? void 0 : options.layout) === 'responsive' && getIsMobileMode(),
                    aboveGlobalLoader: options === null || options === void 0 ? void 0 : options.aboveGlobalLoader
                })
        });
    }, [
        internalContext,
        getBiContext
    ]);
}
